/**
 * Auth Types and Client-Safe Constants
 * 
 * This file contains types and constants that can be safely imported
 * in both client and server components.
 */

import type { MemberRole, OrgPlan } from "@vastipost/shared-types";

/**
 * Auth context for client components
 */
export interface AuthState {
	isAuthenticated: boolean;
	isDevMode: boolean;
	user: {
		id: string;
		name: string;
		email: string;
		/** User's default timezone (IANA string, e.g., "America/New_York") */
		timezone?: string | null;
	} | null;
	org: {
		id: string;
		name: string;
		plan: OrgPlan;
	} | null;
	brand: {
		id: string;
		name: string;
		handle: string;
		/** Brand's timezone override (IANA string). Takes priority over user timezone. */
		timezone?: string | null;
	} | null;
	role: MemberRole;
	/** Email verification state for the EmailVerificationModal */
	emailVerification?: {
		status: "verified" | "awaiting" | "expired" | "failed";
		tokenExpiresAt: string | null;
		sentCount: number;
	} | null;
}

/**
 * Default dev auth state for client rendering
 */
export const DEV_AUTH_STATE: AuthState = {
	isAuthenticated: true,
	isDevMode: true,
	user: {
		id: "b0000001-0001-0001-0001-000000000001",
		name: "Joshua Bigaud",
		email: "joshua@vastipost.dev",
		timezone: "America/New_York", // User's default timezone
	},
	org: {
		id: "a0000001-0001-0001-0001-000000000001",
		name: "Joshua Bigaud",
		plan: "dev",
	},
	brand: {
		id: "c0000001-0001-0001-0001-000000000001",
		name: "EverTale Stories",
		handle: "evertalestories",
		timezone: null, // No brand override, will use user timezone
	},
	role: "owner",
	emailVerification: {
		status: "verified",
		tokenExpiresAt: null,
		sentCount: 0,
	},
};

/**
 * Empty auth state for unauthenticated users
 */
export const EMPTY_AUTH_STATE: AuthState = {
	isAuthenticated: false,
	isDevMode: false,
	user: null,
	org: null,
	brand: null,
	role: "viewer",
	emailVerification: null,
};
